import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Video, UserCheck, Clock, FileText, CheckCircle2, XCircle, Search, Mail } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

const AdminInterviews = () => {
  const { api } = useAdminAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const res = await api().get("/admin/applications");
      const list = Array.isArray(res.data) ? res.data : (res.data?.applications || []);
      const interviewed = list.filter(c => c.interview_details || c.status === "interview_scheduled" || c.status === "interview_completed");
      setCandidates(interviewed);
    } catch (err) {
      console.error("Failed to load interview list:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [api]);

  const filtered = candidates.filter(c => {
    const int = c.interview_details || {};
    const matchesSearch =
      (c.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.position_title || c.job_role || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.application_id || "").toLowerCase().includes(search.toLowerCase());
    const matchesMode = filterMode === "all" || (int.mode || "").toLowerCase().includes(filterMode.toLowerCase());
    return matchesSearch && matchesMode;
  });

  return (
    <div data-testid="admin-interviews">
      <div className="mb-8">
        <h1 className="text-3xl font-light text-white">Interview Schedule</h1>
        <p className="text-sm text-cyan-300/60 mt-1">Manage scheduled candidate interviews, video links, and panel evaluations</p>
      </div>

      {actionFeedback && (
        <div className={`mb-6 p-4 rounded-xl border text-sm ${actionFeedback.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
          {actionFeedback.text}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            placeholder="Search candidate name, role, or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-white/10 bg-black/40 text-sm text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/50"
          />
        </div>
        <div className="flex gap-2">
          {["all", "Teams", "Meet", "In-Person"].map((mode) => (
            <button
              key={mode}
              onClick={() => setFilterMode(mode)}
              className={`px-4 py-2.5 rounded-xl text-xs font-medium uppercase tracking-wider transition-all border ${
                filterMode === mode
                  ? "bg-cyan-400/20 text-cyan-300 border-cyan-400/40"
                  : "bg-white/5 text-white/60 border-white/10 hover:border-white/20"
              }`}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-white/40">Loading interview schedule...</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 rounded-2xl border border-white/10 text-center bg-black/20 text-white/40">
          No candidate interviews found matching criteria.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filtered.map((c) => {
            const int = c.interview_details || {};
            return (
              <motion.div
                key={c.id}
                whileHover={{ y: -2 }}
                className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md relative overflow-hidden"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-cyan-300/60 mb-1">{c.department || "General"}</div>
                    <h3 className="text-lg font-light text-white">{c.full_name}</h3>
                    <div className="text-xs text-white/60">{c.position_title || c.job_role} • {c.application_id}</div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider border ${
                    c.status === "interview_completed"
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                      : "bg-cyan-500/10 border-cyan-500/30 text-cyan-300"
                  }`}>
                    {c.status === "interview_completed" ? "Completed" : "Scheduled"}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-white/80 p-4 rounded-xl bg-black/40 border border-white/5 mb-4">
                  <div className="flex items-center gap-2 text-cyan-300">
                    <Calendar className="w-4 h-4 text-cyan-400" />
                    <span>Date: {int.date || "TBD"}</span>
                    <Clock className="w-4 h-4 text-cyan-400 ml-4" />
                    <span>Time: {int.time || "TBD"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-purple-400" />
                    <span>Mode: {int.mode || "Microsoft Teams"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/60">
                    <UserCheck className="w-4 h-4 text-amber-400" />
                    <span>Interviewer: {int.interviewer || "HR Panel"}</span>
                  </div>
                  {int.meet_link && (
                    <div className="pt-2 text-xs">
                      <a
                        href={int.meet_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-cyan-300 underline hover:text-cyan-200 inline-flex items-center gap-1"
                      >
                        Join Video Call <Video className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-white/40">Email: {c.email}</span>
                  <button
                    onClick={() => setSelectedCandidate(c)}
                    className="px-4 py-2 rounded-lg bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 transition-colors"
                  >
                    View Details & Result
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminInterviews;
